import { Spinner } from '@apexinternal/ui-components'
import { Box, HStack, Stack, Text, VStack } from '@chakra-ui/react'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { isEmpty, startsWith } from 'lodash'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { AccountSearchBar, AccountsTable } from '.'
import { LayoutContext } from '../../containers/contexts/layout/LayoutContext'
import { useAccountSearch } from '../../hooks'
import { MessageCenter } from '../MessageCenter'

const AccountSearchPage = ({
  history,
  location: { search },
}: RouteComponentProps) => {
  const {
    accountSearchState: {
      loadingResults,
      errorMessage,
      accountResults,
      searchTerm,
    },
    searchAccounts,
  } = useAccountSearch()
  const { headerHeight, footerHeight } = useContext(LayoutContext)
  const [tableDimensions, setTableDimensions] = useState({
    height: 0,
    width: 0,
  })
  const accountSearchBarRef = useRef<HTMLDivElement>(null)
  useState(() => {
    if (startsWith(search, '?q=')) {
      const querySearch = decodeURIComponent(search.substring('?q='.length))
      if (querySearch !== searchTerm) {
        searchAccounts(querySearch, history)
      }
    }
  })

  const searchBarHeight = accountSearchBarRef.current?.clientHeight || 0

  useEffect(() => {
    const recalcTableDimensions = () => {
      setTableDimensions({
        height:
          window.innerHeight - headerHeight - footerHeight - searchBarHeight,
        width: window.innerWidth,
      })
    }
    recalcTableDimensions()
    window.addEventListener('resize', recalcTableDimensions)

    return () => {
      window.removeEventListener('resize', recalcTableDimensions)
    }
  }, [headerHeight, footerHeight, searchBarHeight])

  return (
    <VStack spacing={0} flex="1 1 auto" align="stretch">
      <Box ref={accountSearchBarRef}>
        <AccountSearchBar />
      </Box>
      {loadingResults && (
        <VStack justifyContent="center" flex="1 1 auto">
          <Spinner size="lg" />
        </VStack>
      )}
      {!loadingResults && errorMessage && (
        <Box
          sx={{
            '.ui.error.message': {
              'margin-top': '0',
            },
          }}
        >
          <MessageCenter errorMessages={[errorMessage]} />
        </Box>
      )}
      {!loadingResults &&
        !errorMessage &&
        isEmpty(accountResults) &&
        isEmpty(searchTerm) && (
          <Stack
            direction="column"
            flex="1 1 auto"
            alignItems="flex-start"
            paddingX="1rem"
          >
            <Text as="h1" textStyle="h1" pt={12}>
              Account Search
            </Text>
            <Text pt={4}>
              To get started, please enter an account number, name, or SSN in
              the search bar above.
            </Text>
          </Stack>
        )}
      {!loadingResults &&
        !errorMessage &&
        isEmpty(accountResults) &&
        !isEmpty(searchTerm) && (
          <HStack padding="1rem">
            <Text
              as="h1"
              textStyle="h5"
              sx={{
                borderRight: '1px solid',
                borderColor: 'gray.300',
                height: '100%',
                paddingRight: '1rem',
                marginRight: '0.5rem',
              }}
            >
              Search Results
            </Text>
            <Text as="p">
              Your search for "{searchTerm}" returned 0 results
            </Text>
          </HStack>
        )}
      {!loadingResults &&
        !errorMessage &&
        !isEmpty(accountResults) &&
        !isEmpty(searchTerm) && (
          <Stack direction="column" flex="1 1 auto" alignItems="flex-start">
            <AccountsTable
              accountResults={accountResults}
              height={tableDimensions.height}
              width={tableDimensions.width}
            />
          </Stack>
        )}
    </VStack>
  )
}

export default withRouter(AccountSearchPage)
